// Gera Flow JSON estático (sem endpoint) a partir do formulário visual.
// Referência: https://developers.facebook.com/docs/whatsapp/flows/reference/flowjson
export const FLOW_JSON_VERSION = '7.3';
export const MAX_SCREENS = 10;
export const MAX_FIELDS_PER_SCREEN = 20;

export const FLOW_CATEGORIES = [
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'APPOINTMENT_BOOKING',
  'SIGN_UP',
  'SIGN_IN',
  'OTHER',
];

export const FIELD_TYPES = [
  'text',
  'email',
  'phone',
  'number',
  'textarea',
  'date',
  'dropdown',
  'radio',
  'checkbox',
  'optin',
];

const OPTION_TYPES = ['dropdown', 'radio', 'checkbox'];
const TEXT_INPUT_TYPES = {
  text: 'text',
  email: 'email',
  phone: 'phone',
  number: 'number',
};

export const LIMITS = {
  screenTitle: 30,
  heading: 80,
  body: 4096,
  footer: 35,
  fieldLabel: 20,
  choiceLabel: 30,
  dateLabel: 40,
  optinLabel: 120,
  helper: 80,
  optionTitle: 30,
  dropdownOptions: 200,
  groupOptions: 20,
};

let keySeed = 0;
export const uniqueKey = () => {
  keySeed += 1;
  return `k${Date.now().toString(36)}${keySeed}`;
};

export const hasOptions = type => OPTION_TYPES.includes(type);

export const labelLimit = type => {
  if (type === 'date') return LIMITS.dateLabel;
  if (type === 'optin') return LIMITS.optinLabel;
  if (type === 'radio' || type === 'checkbox') return LIMITS.choiceLabel;
  return LIMITS.fieldLabel;
};

export const newField = (type = 'text') => ({
  key: uniqueKey(),
  type,
  label: '',
  required: type !== 'optin',
  helper: '',
  options: hasOptions(type) ? [{ title: '' }, { title: '' }] : [],
});

export const newScreen = () => ({
  key: uniqueKey(),
  title: '',
  heading: '',
  body: '',
  footerLabel: '',
  fields: [newField('text')],
});

export const defaultBuilder = () => ({
  screens: [
    {
      ...newScreen(),
      title: 'Seus dados',
      heading: 'Como podemos falar com você?',
      footerLabel: 'Enviar',
      fields: [
        { ...newField('text'), label: 'Nome completo' },
        { ...newField('phone'), label: 'Telefone' },
        { ...newField('email'), label: 'E-mail', required: false },
      ],
    },
  ],
});

// Letras sem acento, minúsculas e separadas por "_" (sem números), como exigido para nomes de campos.
export const slugify = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/g, '');

const letterSuffix = index => {
  let value = index;
  let suffix = '';
  do {
    suffix = String.fromCharCode(65 + (value % 26)) + suffix;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return suffix;
};

export const screenId = index => `TELA_${letterSuffix(index)}`;

const uniqueName = (base, used) => {
  const fallback = base || 'campo';
  let name = fallback;
  let index = 1;
  while (used.has(name)) {
    name = `${fallback}_${letterSuffix(index).toLowerCase()}`;
    index += 1;
  }
  used.add(name);
  return name;
};

const optionItems = options => {
  const used = new Set();
  return options.map((option, index) => {
    const title = option.title.trim();
    const base = title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);
    const id = uniqueName(base || `opcao_${index + 1}`, used);
    return { id, title };
  });
};

const fieldComponent = (field, name) => {
  const label = field.label.trim();
  const helper = field.helper?.trim();
  if (TEXT_INPUT_TYPES[field.type]) {
    return {
      type: 'TextInput',
      name,
      label,
      'input-type': TEXT_INPUT_TYPES[field.type],
      required: !!field.required,
      ...(helper ? { 'helper-text': helper } : {}),
    };
  }
  if (field.type === 'textarea') {
    return {
      type: 'TextArea',
      name,
      label,
      required: !!field.required,
      ...(helper ? { 'helper-text': helper } : {}),
    };
  }
  if (field.type === 'date') {
    return {
      type: 'DatePicker',
      name,
      label,
      required: !!field.required,
      ...(helper ? { 'helper-text': helper } : {}),
    };
  }
  if (field.type === 'optin') {
    return { type: 'OptIn', name, label, required: !!field.required };
  }
  const componentType = {
    dropdown: 'Dropdown',
    radio: 'RadioButtonsGroup',
    checkbox: 'CheckboxGroup',
  }[field.type];
  return {
    type: componentType,
    name,
    label,
    required: !!field.required,
    'data-source': optionItems(field.options),
  };
};

// Nome reservado do aceite que vira opt-in de comunicações da Talkvia ao ser marcado.
export const TALKVIA_CONSENT_FIELD = 'talkvia_opt_in';

const isTalkviaConsent = field =>
  field.type === 'optin' && !!field.talkviaConsent;

export const buildFlowJson = builder => {
  const used = new Set([TALKVIA_CONSENT_FIELD]);
  const screens = builder.screens.map((screen, index) => ({
    id: screenId(index),
    fields: screen.fields.map(field => ({
      field,
      name: isTalkviaConsent(field)
        ? TALKVIA_CONSENT_FIELD
        : uniqueName(slugify(field.label), used),
    })),
  }));

  const completePayload = {};
  screens.forEach(({ id, fields }, index) => {
    const isLast = index === screens.length - 1;
    fields.forEach(({ name }) => {
      completePayload[name] = isLast
        ? `\${form.${name}}`
        : `\${screen.${id}.form.${name}}`;
    });
  });

  return {
    version: FLOW_JSON_VERSION,
    screens: builder.screens.map((screen, index) => {
      const isLast = index === builder.screens.length - 1;
      const children = [];
      if (screen.heading?.trim()) {
        children.push({ type: 'TextHeading', text: screen.heading.trim() });
      }
      if (screen.body?.trim()) {
        children.push({ type: 'TextBody', text: screen.body.trim() });
      }
      screens[index].fields.forEach(({ field, name }) => {
        children.push(fieldComponent(field, name));
      });
      children.push({
        type: 'Footer',
        label: screen.footerLabel.trim(),
        'on-click-action': isLast
          ? { name: 'complete', payload: completePayload }
          : {
              name: 'navigate',
              next: { type: 'screen', name: screenId(index + 1) },
            },
      });
      return {
        id: screenId(index),
        title: screen.title.trim(),
        ...(isLast ? { terminal: true } : {}),
        layout: { type: 'SingleColumnLayout', children },
      };
    }),
  };
};

const tooLong = (value, limit) => String(value || '').trim().length > limit;

// Retorna códigos de erro para a interface traduzir; lista vazia significa pronto para enviar à Meta.
export const validateBuilder = builder => {
  const errors = [];
  const screens = builder?.screens || [];
  if (!screens.length) errors.push({ code: 'NO_SCREENS' });
  if (screens.length > MAX_SCREENS) errors.push({ code: 'TOO_MANY_SCREENS' });

  screens.forEach((screen, screenIndex) => {
    const screenNumber = screenIndex + 1;
    if (!screen.title?.trim() || tooLong(screen.title, LIMITS.screenTitle)) {
      errors.push({ code: 'SCREEN_TITLE', screen: screenNumber });
    }
    if (tooLong(screen.heading, LIMITS.heading)) {
      errors.push({ code: 'SCREEN_HEADING', screen: screenNumber });
    }
    if (tooLong(screen.body, LIMITS.body)) {
      errors.push({ code: 'SCREEN_BODY', screen: screenNumber });
    }
    if (
      !screen.footerLabel?.trim() ||
      tooLong(screen.footerLabel, LIMITS.footer)
    ) {
      errors.push({ code: 'SCREEN_FOOTER', screen: screenNumber });
    }
    if (
      !screen.fields.length &&
      !screen.heading?.trim() &&
      !screen.body?.trim()
    ) {
      errors.push({ code: 'SCREEN_EMPTY', screen: screenNumber });
    }
    if (screen.fields.length > MAX_FIELDS_PER_SCREEN) {
      errors.push({ code: 'TOO_MANY_FIELDS', screen: screenNumber });
    }
    screen.fields.forEach((field, fieldIndex) => {
      const context = { screen: screenNumber, field: fieldIndex + 1 };
      if (!FIELD_TYPES.includes(field.type)) {
        errors.push({ code: 'FIELD_TYPE', ...context });
        return;
      }
      if (
        !field.label?.trim() ||
        tooLong(field.label, labelLimit(field.type))
      ) {
        errors.push({
          code: 'FIELD_LABEL',
          ...context,
          limit: labelLimit(field.type),
        });
      }
      if (!slugify(field.label) && field.label?.trim()) {
        errors.push({ code: 'FIELD_NAME', ...context });
      }
      if (tooLong(field.helper, LIMITS.helper)) {
        errors.push({ code: 'FIELD_HELPER', ...context });
      }
      if (hasOptions(field.type)) {
        const titles = field.options.map(option => option.title.trim());
        const max =
          field.type === 'dropdown'
            ? LIMITS.dropdownOptions
            : LIMITS.groupOptions;
        if (
          !titles.length ||
          titles.length > max ||
          titles.some(title => !title || title.length > LIMITS.optionTitle) ||
          new Set(titles).size !== titles.length
        ) {
          errors.push({ code: 'FIELD_OPTIONS', ...context });
        }
      }
    });
  });
  return errors;
};

const SCREEN_ID_FORMAT = /^[A-Za-z]+(_[A-Za-z]+)*$/;

// Validação local do JSON avançado, espelhando as regras mínimas do backend.
export const validateFlowJson = json => {
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    return ['NOT_OBJECT'];
  }
  const errors = [];
  if (!json.version) errors.push('NO_VERSION');
  if (!Array.isArray(json.screens) || !json.screens.length) {
    errors.push('NO_SCREENS');
    return errors;
  }
  const ids = json.screens.map(screen => String(screen?.id || ''));
  if (!ids.every(id => SCREEN_ID_FORMAT.test(id))) errors.push('SCREEN_ID');
  if (new Set(ids).size !== ids.length) errors.push('SCREEN_ID_DUPLICATED');
  if (ids.includes('SUCCESS')) errors.push('SCREEN_ID_RESERVED');
  if (!json.screens.some(screen => screen?.terminal === true)) {
    errors.push('NO_TERMINAL');
  }
  return errors;
};

export const parseFlowJsonText = text => {
  try {
    const json = JSON.parse(text);
    return { json, errors: validateFlowJson(json) };
  } catch {
    return { json: null, errors: ['INVALID_JSON'] };
  }
};

// Caminho inverso: monta o construtor visual a partir de um Flow JSON (ex.: colado de outro
// Flow). Só converte o que o construtor sabe gerar; o restante volta em `skipped` (nomes dos
// componentes) para a interface avisar. Telas seguem a ordem do JSON.
const INPUT_TYPES_FROM_JSON = {
  email: 'email',
  phone: 'phone',
  number: 'number',
  text: 'text',
  passcode: 'text',
  password: 'text',
};
const CHOICE_TYPES_FROM_JSON = {
  Dropdown: 'dropdown',
  RadioButtonsGroup: 'radio',
  CheckboxGroup: 'checkbox',
  ChipsSelector: 'checkbox',
};
const TEXT_APPEND_TYPES = ['TextSubheading', 'TextCaption', 'RichText'];

const flattenChildren = children =>
  (Array.isArray(children) ? children : []).flatMap(child =>
    child?.type === 'Form' ? flattenChildren(child.children) : [child]
  );

const textOf = value => (typeof value === 'string' ? value.trim() : '');

const fieldFromComponent = component => {
  const base = {
    key: uniqueKey(),
    label: textOf(component.label),
    required: !!component.required,
    helper: textOf(component['helper-text']),
    options: [],
  };
  if (component.type === 'TextInput') {
    return {
      ...base,
      type: INPUT_TYPES_FROM_JSON[component['input-type']] || 'text',
    };
  }
  if (component.type === 'TextArea') return { ...base, type: 'textarea' };
  if (['DatePicker', 'CalendarPicker'].includes(component.type)) {
    return { ...base, type: 'date' };
  }
  if (component.type === 'OptIn') {
    return {
      ...base,
      type: 'optin',
      helper: '',
      talkviaConsent: component.name === TALKVIA_CONSENT_FIELD,
    };
  }
  const choice = CHOICE_TYPES_FROM_JSON[component.type];
  if (choice && Array.isArray(component['data-source'])) {
    return {
      ...base,
      type: choice,
      helper: '',
      options: component['data-source'].map(item => ({
        title: textOf(item?.title),
      })),
    };
  }
  return null;
};

export const builderFromFlowJson = json => {
  const sourceScreens = Array.isArray(json?.screens) ? json.screens : [];
  if (!sourceScreens.length) return null;
  const skipped = new Set();
  if (sourceScreens.length > MAX_SCREENS) skipped.add('screens');

  const screens = sourceScreens.slice(0, MAX_SCREENS).map(source => {
    const screen = { ...newScreen(), title: textOf(source?.title), fields: [] };
    const bodyParts = [];
    flattenChildren(source?.layout?.children).forEach(component => {
      const type = component?.type;
      if (type === 'TextHeading' && !screen.heading) {
        screen.heading = textOf(component.text);
      } else if (type === 'TextBody' || TEXT_APPEND_TYPES.includes(type)) {
        const text = Array.isArray(component.text)
          ? component.text.join('\n')
          : textOf(component.text);
        if (text) bodyParts.push(text);
      } else if (type === 'Footer') {
        screen.footerLabel = textOf(component.label);
      } else {
        const field = fieldFromComponent(component || {});
        if (field && screen.fields.length < MAX_FIELDS_PER_SCREEN) {
          screen.fields.push(field);
        } else if (type) {
          skipped.add(type);
        }
      }
    });
    screen.body = bodyParts.join('\n\n');
    return screen;
  });

  return { builder: { screens }, skipped: [...skipped] };
};
